import { useState, useEffect, lazy, Suspense, useRef } from "react";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import SEOHead from "@/components/SEOHead";
import StickyMobileCTA from "@/components/StickyMobileCTA";
import DesktopFloatingCTA from "@/components/DesktopFloatingCTA";
import { analytics } from "@/lib/analytics";

// Lazy load below-fold sections for faster initial load
const TrustedBySection = lazy(() => import("@/components/TrustedBySection"));
const NotificationProofSection = lazy(() => import("@/components/NotificationProofSection"));
const ProblemSection = lazy(() => import("@/components/ProblemSection"));
const TargetAudienceSection = lazy(() => import("@/components/TargetAudienceSection"));
const WhyUsSection = lazy(() => import("@/components/WhyUsSection"));
const SocialProofSection = lazy(() => import("@/components/SocialProofSection"));
const HowItWorksSection = lazy(() => import("@/components/HowItWorksSection"));
const PricingSection = lazy(() => import("@/components/PricingSection"));
const ROICalculator = lazy(() => import("@/components/ROICalculator"));
const WhatYouGetSection = lazy(() => import("@/components/WhatYouGetSection"));
const FAQSection = lazy(() => import("@/components/FAQSection"));
const FinalPushSection = lazy(() => import("@/components/FinalPushSection"));
const Footer = lazy(() => import("@/components/Footer"));

// Lightweight skeleton for lazy sections
const SectionSkeleton = ({ height = "h-96" }: { height?: string }) => (
  <div className={`${height} w-full animate-pulse bg-gradient-to-b from-background/50 to-background/20`} />
);

const Index = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const trackedSections = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Faster cinematic page load sequence
    const timer1 = setTimeout(() => setIsLoaded(true), 50);
    const timer2 = setTimeout(() => setShowContent(true), 150);
    
    // Track page view
    analytics.pageView('homepage');
    
    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  // Section visibility tracking
  useEffect(() => {
    const sections = [
      'hero', 'trusted-by', 'notifications', 'problem', 
      'target-audience', 'why-us', 'social-proof', 'how-it-works',
      'pricing', 'roi-calculator', 'what-you-get', 'faq', 'final-push'
    ];

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionId = entry.target.getAttribute('data-section');
            if (sectionId && !trackedSections.current.has(sectionId)) {
              trackedSections.current.add(sectionId);
              const scrollDepth = Math.round((window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100);
              analytics.sectionViewed(sectionId, scrollDepth);
            }
          }
        });
      },
      { threshold: 0.3 }
    );

    // Wait for content to load then observe sections
    const timer = setTimeout(() => {
      sections.forEach((section) => {
        const el = document.querySelector(`[data-section="${section}"]`);
        if (el) observer.observe(el);
      });
    }, 500);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, []);

  return (
    <>
      <SEOHead />
      
      {/* Cinematic curtain reveal */}
      <div 
        className={`fixed inset-0 z-[100] bg-background pointer-events-none transition-all duration-500 ease-out ${
          isLoaded ? 'opacity-0' : 'opacity-100'
        }`}
        style={{ 
          clipPath: isLoaded ? 'inset(100% 0 0 0)' : 'inset(0 0 0 0)',
        }}
      >
        {/* Fuel-themed loader */}
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
          <div className="text-4xl animate-flame-flicker">🚀</div>
          <div className="w-32 h-1 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-neon-purple to-neon-pink animate-fuel-loader" />
          </div>
          <span className="text-sm text-muted-foreground animate-pulse">Fueling up...</span>
        </div>
      </div>
      
      <Navbar />
      
      <main 
        className={`min-h-screen gradient-mesh transition-opacity duration-300 ${
          showContent ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {/* Hero loads immediately - critical path */}
        <div className={showContent ? 'animate-section-entrance' : ''} data-section="hero">
          <Hero />
        </div>
        
        {/* Lazy loaded sections with suspense boundaries */}
        <Suspense fallback={<SectionSkeleton height="h-24" />}>
          <div className={showContent ? 'animate-section-entrance' : ''} style={{ animationDelay: '50ms' }} data-section="trusted-by">
            <TrustedBySection />
          </div>
        </Suspense>
        
        <Suspense fallback={<SectionSkeleton height="h-64" />}>
          <div className={showContent ? 'animate-section-entrance' : ''} style={{ animationDelay: '100ms' }} data-section="notifications">
            <NotificationProofSection />
          </div>
        </Suspense>
        
        <Suspense fallback={<SectionSkeleton />}>
          <div className={showContent ? 'animate-section-entrance' : ''} style={{ animationDelay: '150ms' }} data-section="problem">
            <ProblemSection />
          </div>
        </Suspense>
        
        <Suspense fallback={<SectionSkeleton height="h-48" />}>
          <div className={showContent ? 'animate-section-entrance' : ''} style={{ animationDelay: '200ms' }} data-section="target-audience">
            <TargetAudienceSection />
          </div>
        </Suspense>
        
        <Suspense fallback={<SectionSkeleton height="h-32" />}>
          <div className={showContent ? 'animate-section-entrance' : ''} style={{ animationDelay: '250ms' }} data-section="why-us">
            <WhyUsSection />
          </div>
        </Suspense>
        
        <Suspense fallback={<SectionSkeleton />}>
          <div className={showContent ? 'animate-section-entrance' : ''} style={{ animationDelay: '300ms' }} data-section="social-proof">
            <SocialProofSection />
          </div>
        </Suspense>
        
        <Suspense fallback={<SectionSkeleton />}>
          <div className={showContent ? 'animate-section-entrance' : ''} style={{ animationDelay: '350ms' }} data-section="how-it-works">
            <HowItWorksSection />
          </div>
        </Suspense>
        
        <Suspense fallback={<SectionSkeleton />}>
          <div className={showContent ? 'animate-section-entrance' : ''} style={{ animationDelay: '400ms' }} data-section="pricing">
            <PricingSection />
          </div>
        </Suspense>
        
        <Suspense fallback={<SectionSkeleton />}>
          <div className={showContent ? 'animate-section-entrance' : ''} style={{ animationDelay: '450ms' }} data-section="roi-calculator">
            <ROICalculator />
          </div>
        </Suspense>
        
        <Suspense fallback={<SectionSkeleton />}>
          <div className={showContent ? 'animate-section-entrance' : ''} style={{ animationDelay: '500ms' }} data-section="what-you-get">
            <WhatYouGetSection />
          </div>
        </Suspense>
        
        <Suspense fallback={<SectionSkeleton />}>
          <div className={showContent ? 'animate-section-entrance' : ''} style={{ animationDelay: '550ms' }} data-section="faq">
            <FAQSection />
          </div>
        </Suspense>
        
        <Suspense fallback={<SectionSkeleton />}>
          <div className={showContent ? 'animate-section-entrance' : ''} style={{ animationDelay: '600ms' }} data-section="final-push">
            <FinalPushSection />
          </div>
        </Suspense>
        
        <Suspense fallback={<SectionSkeleton height="h-48" />}>
          <Footer />
        </Suspense>
        
        <StickyMobileCTA />
        <DesktopFloatingCTA />
      </main>
    </>
  );
};

export default Index;
